import { monthlyCategoryAxis, monthlyPlotGrid, monthlyDateLabel } from './monthly_trend_axis.js'

/**
 * Stored legacy-mobile health semantics, not the newer age/pace_of_aging v2 DTO.
 * Adapted from miniTaro agingPaceChart.ts / recoveryScoreTrendChart.ts and native
 * age_simple_chart.js. Research deliberately keeps ALL 30 daily dates visible
 * (no 14-day trimming/zoom, no monthly averaging). No current-profile age line:
 * a historical report must never borrow the currently bound person's birthdate.
 * See docs/research-month-health-trends.md for exact source revisions/contracts.
 */
function number(value, { positive = false, score = false } = {}) {
  if (!['number', 'string'].includes(typeof value) || value === '' ||
      (typeof value === 'string' && !value.trim())) return null
  const n = Number(value)
  if (!Number.isFinite(n) || (positive && n <= 0) || (score && (n < 0 || n > 100))) return null
  return n
}
function first(values, validation) {
  for (const v of values) {
    const n = number(v, validation)
    if (n !== null) return n
  }
  return null
}
const rounded = (n, digits) => n === null ? null : Number(n.toFixed(digits))

export function healthTrendMetrics(report = {}) {
  const aging = report?.aging_pace || {}
  return {
    age: rounded(first([report.sleep_age_avg_21day], { positive: true }), 1),
    raw: rounded(first([aging.aging_pace_raw, report.aging_pace_raw]), 2),
    avg28d: rounded(first([aging.aging_pace_avg_28d, report.aging_pace_avg_28d]), 2),
    recovery: rounded(first([report.recoveryScore, report.recovery_score], { score: true }), 0),
  }
}

export function buildHealthTrendDays(reports, anchor) {
  // Calendar keys, not UTC instants: timestamp-shaped legacy keys retain their date.
  const dateKey = typeof anchor === 'string' ? anchor.match(/^(\d{4}-\d{2}-\d{2})(?:$|[ T])/)?.[1] : null
  const end = dateKey && new Date(`${dateKey}T12:00:00Z`)
  if (!end || !Number.isFinite(end.getTime()) || end.toISOString().slice(0, 10) !== dateKey) return []
  return Array.from({ length: 30 }, (_, i) => {
    const date = new Date(end)
    date.setUTCDate(date.getUTCDate() - 29 + i)
    const key = date.toISOString().slice(0, 10)
    const short = `${date.getUTCMonth() + 1}/${date.getUTCDate()}`
    const report = reports?.[short]
    // The legacy converter uses M/D keys; keep the full date as an extra guard.
    const sameDay = report && (!report.date || report.date === key)
    return { date: key, ...healthTrendMetrics(sameDay ? report : {}) }
  })
}

const fmt = (v, digits) => typeof v === 'number' && Number.isFinite(v) ? v.toFixed(digits) : '--'
function base(points, series, digits, unit) {
  return {
    animationDuration: 300,
    backgroundColor: 'transparent',
    grid: { top: 30, ...monthlyPlotGrid },
    tooltip: {
      trigger: 'axis', confine: true, backgroundColor: '#FEFEFE', borderColor: '#E2E8F0',
      textStyle: { fontSize: 12, color: '#0B0F19', textShadowBlur: 0 },
      formatter: params => {
        if (!params?.length) return ''
        const index = params[0].dataIndex ?? points.findIndex(p => monthlyDateLabel(p.date) === params[0].axisValue)
        const date = points[index]?.date || params[0].axisValue
        return [date, ...series.map((s, i) =>
          `${s.name}: ${fmt(params.find(p => p.seriesIndex === i)?.value, digits)}${unit ? ` ${unit}` : ''}`)].join('<br/>')
      },
    },
    legend: { show: false },
    xAxis: monthlyCategoryAxis(points.map(p => p.date)),
    yAxis: {
      type: 'value', name: unit, nameTextStyle: { color: '#94A3B8', fontSize: 10 }, nameGap: 10,
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { fontSize: 10, color: '#94A3B8' },
      splitLine: { lineStyle: { color: '#E8EDF5', type: 'dashed' } },
    },
    series,
  }
}
function line(name, values, color, extra = {}) {
  return { name, type: 'line', data: values, connectNulls: false, symbol: 'circle', symbolSize: 5,
    smooth: false, itemStyle: { color }, lineStyle: { color, width: 2 }, ...extra }
}

export function buildSleepAgeTrendOption(points, labels) {
  const series = [line(labels.age, points.map(p => p.age), '#5B8FF9', {
    // Every measured day must remain a hover target, even on a crowded month axis.
    showSymbol: true, showAllSymbol: true,
  })]
  const option = base(points, series, 1, labels.years)
  // Bind to the measured point rather than the axis (which also covers null days).
  // Read the displayed series value, without recalculating or coercing missing data.
  Object.assign(option.tooltip, {
    show: true, showContent: true, trigger: 'item', triggerOn: 'mousemove|click',
    formatter: params => {
      const point = Array.isArray(params) ? params[0] : params
      if (!point) return ''
      const index = point.dataIndex ?? points.findIndex(p => monthlyDateLabel(p.date) === point.axisValue)
      const date = points[index]?.date
      if (!date) return ''
      return `${date}<br/>${labels.age}: ${fmt(point.value, 1)} ${labels.years}`
    },
  })
  const values = points.map(p => p.age).filter(v => v !== null)
  let min = 20, max = 80
  if (values.length) {
    const low = Math.min(...values), high = Math.max(...values)
    const range = high - low || 10, pad = Math.max(4, range * 0.6)
    min = Math.floor((low - pad) / 5) * 5
    max = Math.ceil((high + pad) / 5) * 5
  }
  Object.assign(option.yAxis, { min, max })
  return option
}

export function buildAgingPaceTrendOption(points, labels) {
  const series = [
    line(labels.pace, points.map(p => p.raw), '#5B8FF9'),
    line(labels.avg28d, points.map(p => p.avg28d), '#9D87CB', { smooth: true, symbol: 'none' }),
  ]
  const option = base(points, series, 2, labels.yearsPerYear)
  option.legend = { show: true, top: 0, itemWidth: 14, textStyle: { fontSize: 10 } }
  Object.assign(option.grid, { top: 48 })
  Object.assign(option.yAxis, { min: -2, max: 3, axisLabel: {
    fontSize: 10, color: '#94A3B8', formatter: v => Number(v).toFixed(1),
  } })
  return option
}

function recoveryRange(values) {
  const vals = values.filter(v => v !== null)
  if (!vals.length) return { min: 50, max: 95, interval: 10 }
  const low = Math.min(...vals), high = Math.max(...vals), pad = high - low < 20 ? 10 : 5
  let min = Math.max(0, Math.floor((low - pad) / 10) * 10)
  let max = Math.min(100, Math.ceil((high + pad) / 10) * 10)
  if (max - min < 30) {
    min = Math.floor(((low + high) / 2 - 15) / 10) * 10
    max = min + 30
    if (min < 0) { min = 0; max = 30 }
    if (max > 100) { max = 100; min = 70 }
  }
  return { min, max, interval: 10 }
}
export function buildRecoveryScoreTrendOption(points, labels) {
  const values = points.map(p => p.recovery)
  const series = [line(labels.recovery, values.map(value => value === null ? { value: null } : {
    value, itemStyle: { color: value >= 80 ? '#2dd4bf' : value >= 60 ? '#FFBA2F' : '#E92D2E',
      borderColor: '#FFFFFF', borderWidth: 2, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.10)' },
  }), '#C9D2E2', { smooth: 0.55, smoothMonotone: 'x', symbolSize: 10 })]
  const option = base(points, series, 0, '')
  Object.assign(option.yAxis, recoveryRange(values))
  return option
}
